/**
 * Bookshop Phase A — Pending Payments queue.
 * Staff review manual (bank_transfer / ecocash / cash_on_collection) orders
 * once the customer submits a confirmation code.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Wallet, CheckCircle2, XCircle } from 'lucide-react';
import api from '@/lib/api';

const getErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || String(x)).join(' · ');
  return d || e?.message || 'Error';
};

const METHOD_LABEL = {
  bank_transfer: 'Bank transfer', ecocash: 'EcoCash', cash_on_collection: 'Cash on collection',
};

export const PendingPayments = () => {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['bookshop-pending-payments'],
    queryFn: async () => (await api.get('/bookshop/admin/pending-payments')).data,
    refetchInterval: 15000,
  });
  const rows = data?.orders || [];

  const confirm = useMutation({
    mutationFn: async ({ id, notes }) => (await api.post(`/bookshop/admin/pending-payments/${id}/confirm`, { notes })).data,
    onSuccess: (res) => { toast.success(`Payment confirmed — ${res.granted || 0} entitlement(s) granted`); qc.invalidateQueries({ queryKey: ['bookshop-pending-payments'] }); setExpanded(null); setNotes(''); },
    onError: (e) => toast.error(getErr(e)),
  });
  const reject = useMutation({
    mutationFn: async ({ id, reason }) => (await api.post(`/bookshop/admin/pending-payments/${id}/reject`, { reason })).data,
    onSuccess: () => { toast.success('Payment rejected'); qc.invalidateQueries({ queryKey: ['bookshop-pending-payments'] }); setExpanded(null); setReason(''); },
    onError: (e) => toast.error(getErr(e)),
  });

  return (
    <div className="space-y-3" data-testid="bookshop-pending-payments">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Wallet className="h-4 w-4" /> Pending manual payments</CardTitle>
          <CardDescription className="text-xs">Orders paid via bank transfer, EcoCash or cash-on-collection wait here until you reconcile the payment on your side.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? <Loader2 className="animate-spin mx-auto" /> : rows.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-8">No pending payments.</p>
          ) : (
            <ul className="space-y-2">
              {rows.map((o) => (
                <li key={o.id} className="border rounded p-3 text-xs" data-testid={`pending-row-${o.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <span className="font-semibold text-sm">{o.order_number}</span>
                      <Badge variant="outline" className="ml-2 text-[10px]">{METHOD_LABEL[o.payment_method] || o.payment_method}</Badge>
                      <Badge variant="outline" className="ml-1 text-[10px]">{o.payment_status}</Badge>
                      <span className="text-slate-500 ml-2">{o.currency || 'GBP'} {Number(o.total).toFixed(2)}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => confirm.mutate({ id: o.id, notes })} data-testid={`pending-confirm-${o.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark paid</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { setExpanded(expanded === o.id ? null : o.id); setReason(''); setNotes(''); }} data-testid={`pending-review-${o.id}`}>Review / reject</Button>
                    </div>
                  </div>
                  <div className="mt-1 text-slate-600">
                    <span>{o.customer_name || '—'}</span> · <span>{o.customer_email || '—'}</span>
                    {o.confirmation_code && <> · <span>ref: <strong>{o.confirmation_code}</strong></span></>}
                  </div>
                  <ul className="mt-1 text-slate-500">
                    {(o.items || []).map((li, i) => <li key={i}>· {li.product_name} × {li.qty} ({li.access_type})</li>)}
                  </ul>
                  {expanded === o.id && (
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <label className="text-slate-500">Optional notes (visible to staff only)</label>
                        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`pending-notes-${o.id}`} />
                      </div>
                      <div>
                        <label className="text-slate-500">Rejection reason (required to reject)</label>
                        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`pending-reason-${o.id}`} />
                        <Button size="sm" variant="destructive" className="mt-1" onClick={() => reject.mutate({ id: o.id, reason })} data-testid={`pending-reject-${o.id}`}><XCircle className="h-3.5 w-3.5 mr-1" /> Reject payment</Button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PendingPayments;
